/**
 * System Status — read-only state of THIS environment.
 *
 * The screen an admin opens instead of asking someone to run a script: which
 * build is live, whether this is preview or production, which migrations have
 * run, whether the money backfill is complete here, and whether Mongo can do
 * multi-document transactions.
 *
 * There is nothing to click that changes anything, deliberately. A check that
 * cannot be answered says "Unknown" and gives the reason — never a zero
 * standing in for "could not look".
 */
import React, { useEffect, useState, useCallback } from 'react';
import { systemStatusAPI } from '../lib/api';
import api from '../lib/api';
import { Link } from 'react-router-dom';
import { useCap } from '../components/auth/RequireCap';
import AccessState from '../components/auth/AccessState';
import EmployeeNumberingCard from '../components/system/EmployeeNumberingCard';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Activity, RefreshCw, CheckCircle2, XCircle, HelpCircle, Database, AlertTriangle,
  GitCommitHorizontal, Coins, Eye, FlaskConical, KeyRound, ShieldAlert, ScrollText, Hash,
  LayoutGrid, Mail, HardDrive, Wrench, UserRound, ExternalLink, Gavel,
} from 'lucide-react';

// Collections whose disagreements are effective-dated payroll history and so can
// be ruled 'reviewed, superseded' when a newer active record has replaced them.
const RULABLE_COLLECTIONS = new Set(['federal_w4_records', 'state_withholding_records']);

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

// Admin-only. Turns the opaque record id on a disagreement row into the
// employee it belongs to, with a deep link to their W-4 screen, so a person can
// act on it without looking up an id. `employee` is null for rows the backend
// could not (or is not allowed to) resolve — the cell then shows a dash.
function EmployeeCell({ employee }) {
  if (!employee) return <span className="text-slate-400">—</span>;
  return (
    <div className="text-sm" data-testid="disagreement-employee">
      <div className="font-medium flex items-center gap-1">
        <UserRound className="h-3.5 w-3.5 text-slate-400" />
        {employee.name || 'Unknown'}
      </div>
      <div className="text-xs text-slate-500">#{employee.employee_number || '—'}</div>
      {employee.link && (
        <Link
          to={employee.link}
          className="mt-0.5 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
          data-testid="disagreement-w4-link"
        >
          <ExternalLink className="h-3 w-3" />Open W-4
        </Link>
      )}
    </div>
  );
}

function StatusPill({ ok, okLabel, badLabel, unknown }) {
  if (unknown) {
    return <Badge variant="outline" className="border-slate-400 text-slate-600" data-testid="status-unknown">
      <HelpCircle className="w-3 h-3 mr-1" />Unknown</Badge>;
  }
  return ok
    ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"><CheckCircle2 className="w-3 h-3 mr-1" />{okLabel}</Badge>
    : <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />{badLabel}</Badge>;
}

function UnknownNote({ reason }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-slate-300 bg-slate-50 p-3 text-sm text-slate-700 dark:bg-slate-900/40">
      <HelpCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <span><strong>Unknown.</strong> {reason}</span>
    </div>
  );
}

/** Who holds a view-only capability, and why.
 *
 *  Shared by the rate-visibility panel and the grid-access panel because both
 *  answer the same question off the same payload shape. Two copies of this
 *  markup is how one of them stops saying "granted to this person, not to the
 *  role" - which is the only line on it that matters.
 *
 *  Role holders are counted, never named: 49 accounts one per line is a wall
 *  nobody reads. INDIVIDUAL grants are named in full, because a privilege
 *  given to one person is invisible unless it is spelled out.
 */
function HoldersPanel({ data, prefix }) {
  if (data.status === 'unknown') return <UnknownNote reason={data.reason} />;
  return (
    <>
      <div className="space-y-3">
        {data.modules.map((m) => {
          const granted = m.holders.filter((h) => h.individual_grant).length;
          return (
            <div key={m.module} className="rounded-lg border p-3" data-testid={`${prefix}-${m.module}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">{m.label}</span>
                <span className="text-xs text-slate-500">
                  {m.holders.length} active account{m.holders.length === 1 ? '' : 's'}
                  {granted > 0 ? ` · ${granted} by individual grant` : ''}
                  {m.deactivated.length > 0 ? ` · ${m.deactivated.length} deactivated` : ''}
                </span>
              </div>
              <p className="mt-1 text-xs text-slate-500">Shows: {m.means}</p>
              <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">
                {m.by_role.length > 0
                  ? <>By role: {m.by_role.map((r) => `${r.accounts} × ${r.role}`).join(' · ')}</>
                  : 'No role grants this.'}
              </p>
              <ul className="mt-2 space-y-1 text-xs text-slate-600 dark:text-slate-400">
                {m.individual_grants.length === 0 && (
                  <li className="text-slate-500">No individual grants - nobody holds this outside their role.</li>
                )}
                {m.individual_grants.map((h) => (
                  <li key={`${m.module}-${h.email}`}>
                    <span className="font-medium">{h.name}</span>
                    {' '}<span className="text-slate-400">{h.email}</span>
                    {' · '}{h.role} role
                    <strong className="text-amber-700 dark:text-amber-500"> - granted to this person, not to the role</strong>
                  </li>
                ))}
              </ul>
              {m.deactivated.length > 0 && (
                <p className="mt-1 text-xs text-slate-400" data-testid={`${prefix}-deactivated-${m.module}`}>
                  {m.deactivated.length} deactivated account{m.deactivated.length === 1 ? '' : 's'} would hold this, and no longer can
                </p>
              )}
            </div>
          );
        })}
      </div>
      <p className="mt-3 text-xs text-slate-500" data-testid={`${prefix}-note`}>{data.note}</p>
    </>
  );
}


/** Feature availability — which env settings are present/missing (never values)
 *  and which features self-disabled. Admin-only data from /system/feature-status. */
function SsnKeyRotationCard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [running, setRunning] = useState(false);
  const load = useCallback(() => {
    api.get('/system/ssn-reencryption')
      .then(r => setData(r.data))
      .catch(() => setErr(true));
  }, []);
  useEffect(() => { load(); }, [load]);
  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post('/system/ssn-reencryption/run');
      setData(r.data.status);
      toast.success('Re-encryption run complete');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start re-encryption');
    } finally { setRunning(false); }
  };
  if (err || !data) return null;
  const keys = data.keys || {};
  const yn = (v) => v === true ? 'Yes' : v === false ? 'No' : '—';
  const validBadge = (present, valid) => !present
    ? <Badge variant="outline" className="border-slate-300 text-slate-500">Not configured</Badge>
    : valid
      ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"><CheckCircle2 className="w-3 h-3 mr-1" />Valid</Badge>
      : <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Invalid key</Badge>;
  const Stat = ({ label, value, testid }) => (
    <div className="rounded-lg border p-3" data-testid={testid}>
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-2xl font-semibold text-slate-900">{value === null || value === undefined ? '—' : value}</p>
    </div>
  );
  return (
    <Card data-testid="ssn-key-rotation-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><KeyRound className="w-4 h-4" /> SSN encryption key rotation</CardTitle>
        <CardDescription>
          Progress of re-encrypting sensitive fields onto the current primary key. Counts only — values are never shown.
          Clear the "values on previous key" to zero (with no failures) before removing the previous key.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Stat label="On primary key" value={data.values_on_primary_key} testid="ssn-on-primary" />
          <Stat label="On previous key" value={data.values_on_previous_key} testid="ssn-on-previous" />
          <Stat label="Failed" value={data.values_failed} testid="ssn-failed" />
        </div>
        <Table>
          <TableBody>
            <TableRow><TableCell className="text-sm text-slate-600">Previous key configured</TableCell>
              <TableCell data-testid="ssn-previous-configured">{yn(data.previous_key_configured)}</TableCell></TableRow>
            <TableRow><TableCell className="text-sm text-slate-600">Primary key</TableCell>
              <TableCell data-testid="ssn-primary-valid">{validBadge(keys.primary_present, keys.primary_valid)}</TableCell></TableRow>
            <TableRow><TableCell className="text-sm text-slate-600">Previous key</TableCell>
              <TableCell data-testid="ssn-previous-valid">{validBadge(keys.previous_present, keys.previous_valid)}</TableCell></TableRow>
            <TableRow><TableCell className="text-sm text-slate-600">Last run</TableCell>
              <TableCell className="text-sm" data-testid="ssn-last-run">{data.last_run_at ? `${new Date(data.last_run_at).toLocaleString()} — ${data.last_run_result || 'n/a'}` : 'Never run'}</TableCell></TableRow>
          </TableBody>
        </Table>
        <Button onClick={runNow} disabled={running} data-testid="ssn-run-now-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${running ? 'animate-spin' : ''}`} />
          {running ? 'Running…' : 'Run now'}
        </Button>
      </CardContent>
    </Card>
  );
}

function ObjectStorageCard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [running, setRunning] = useState(false);
  const load = useCallback(() => {
    api.get('/system/object-storage')
      .then(r => setData(r.data))
      .catch(() => setErr(true));
  }, []);
  useEffect(() => { load(); }, [load]);
  const runNow = async () => {
    setRunning(true);
    try {
      await api.post('/system/object-storage/run');
      toast.success('Manifest started — refreshing shortly');
      setTimeout(load, 45000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start the manifest');
      setRunning(false);
      return;
    }
    setTimeout(() => setRunning(false), 45000);
  };
  if (err || !data) return null;
  const Stat = ({ label, value, testid, tone }) => (
    <div className="rounded-lg border p-3" data-testid={testid}>
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-2xl font-semibold ${tone || 'text-slate-900'}`}>{value === null || value === undefined ? '—' : value}</p>
    </div>
  );
  const missing = data.missing_count;
  return (
    <Card data-testid="object-storage-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><HardDrive className="w-4 h-4" /> File storage (object manifest)</CardTitle>
        <CardDescription>
          Daily sweep of every stored file — credential photos and GSOC documents. It records each
          object's key, size and SHA-256 and checks it still exists. Files are write-once and are
          never overwritten or deleted by normal use. "Missing objects" must stay zero.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Stat label="Objects tracked" value={data.object_count} testid="obj-count" />
          <Stat label="Missing objects" value={data.missing_count}
                tone={missing ? 'text-red-600' : 'text-emerald-600'} testid="obj-missing" />
          <Stat label="Temporarily unreadable" value={data.unavailable_count}
                tone={data.unavailable_count ? 'text-amber-600' : 'text-slate-900'} testid="obj-unavailable" />
        </div>
        {data.test_missing_count > 0 && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="obj-test-missing-list">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">
              Test data — excluded from the missing count ({data.test_missing_count})
            </p>
            <p className="text-xs text-slate-500 mb-2">
              Records flagged as test data that point at a placeholder object. These are not real
              uploads and are not data loss. They stay out of "Missing objects".
            </p>
            <ul className="text-xs font-mono text-slate-700 space-y-1">
              {(data.test_missing || []).slice(0, 10).map((m, i) => (
                <li key={i}>{m.source} · {m.record_id}</li>
              ))}
            </ul>
          </div>
        )}
        <Table>
          <TableBody>
            <TableRow><TableCell className="text-sm text-slate-600">Verified present</TableCell>
              <TableCell data-testid="obj-present">{data.present_count ?? '—'}</TableCell></TableRow>
            <TableRow><TableCell className="text-sm text-slate-600">Last manifest run</TableCell>
              <TableCell className="text-sm" data-testid="obj-last-run">{data.last_manifest_run ? new Date(data.last_manifest_run).toLocaleString() : 'Never run'}</TableCell></TableRow>
          </TableBody>
        </Table>
        {missing > 0 && (data.missing || []).length > 0 && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="obj-missing-list">
            <p className="text-xs font-semibold uppercase tracking-wide text-red-700 mb-1">Records pointing at a missing object</p>
            <ul className="text-xs font-mono text-red-800 space-y-1">
              {(data.missing || []).slice(0, 10).map((m, i) => (
                <li key={i}>{m.source} · {m.record_id}</li>
              ))}
            </ul>
          </div>
        )}
        <Button onClick={runNow} disabled={running} data-testid="obj-run-now-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${running ? 'animate-spin' : ''}`} />
          {running ? 'Running…' : 'Run manifest now'}
        </Button>
      </CardContent>
    </Card>
  );
}


function FeatureStatusCard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let on = true;
    api.get('/system/feature-status')
      .then(r => { if (on) setData(r.data); })
      .catch(() => { if (on) setErr(true); });
    return () => { on = false; };
  }, []);
  if (err) return null;
  if (!data) return null;
  const critical = (data.settings || []).filter(s => s.group === 'critical');
  const optional = (data.settings || []).filter(s => s.group === 'optional');
  const Row = ({ s }) => (
    <TableRow data-testid={`setting-row-${s.key}`}>
      <TableCell className="font-mono text-xs">{s.key}</TableCell>
      <TableCell className="text-sm">{s.feature}</TableCell>
      <TableCell>
        {s.present
          ? (s.weak
              ? <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100" data-testid={`setting-weak-${s.key}`}><AlertTriangle className="w-3 h-3 mr-1" />Present, weak ({s.weak_reason})</Badge>
              : <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"><CheckCircle2 className="w-3 h-3 mr-1" />Present</Badge>)
          : <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Missing</Badge>}
      </TableCell>
    </TableRow>
  );
  return (
    <Card data-testid="feature-status-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><KeyRound className="w-4 h-4" /> Feature Availability</CardTitle>
        <CardDescription>
          Which environment settings this deployment has, and which features are available. Values are never shown.
          Missing an optional setting disables only its feature — it never stops the app from starting.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2" data-testid="feature-availability-badges">
          {(data.features || []).map(f => (
            <Badge key={f.id} variant="outline"
                   className={f.available ? 'border-emerald-300 text-emerald-700' : (f.critical ? 'border-red-400 text-red-700' : 'border-amber-400 text-amber-700')}
                   title={f.available ? 'Available' : `Unavailable — needs ${f.missing.join(', ')}`}
                   data-testid={`feature-badge-${f.id}`}>
              {f.available ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
              {f.name}{!f.available && ` — needs ${f.missing.join(', ')}`}
            </Badge>
          ))}
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Critical settings</p>
          <Table><TableHeader><TableRow><TableHead>Setting</TableHead><TableHead>Feature</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>{critical.map(s => <Row key={s.key} s={s} />)}</TableBody></Table>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Optional settings</p>
          <Table><TableHeader><TableRow><TableHead>Setting</TableHead><TableHead>Feature</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>{optional.map(s => <Row key={s.key} s={s} />)}</TableBody></Table>
        </div>
      </CardContent>
    </Card>
  );
}


function SignatureIntegrityCard() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/evaluations/admin/signature-integrity'); setReport(data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not read the signature integrity report'); }
    finally { setLoading(false); }
  }, []);
  return (
    <Card data-testid="signature-integrity-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><ShieldAlert className="w-4 h-4" /> Evaluation signature integrity</CardTitle>
        <CardDescription>
          Evaluations where an employee signature was recorded by a logged-in staff user instead of the employee's own emailed link. Read-only — review with HR. The same report opens from the <Link to="/evaluations" className="text-blue-600 hover:underline" data-testid="signature-integrity-eval-link">Evaluations page</Link>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button variant="outline" onClick={run} disabled={loading} data-testid="signature-integrity-run-btn">
          <ShieldAlert className={`mr-2 h-4 w-4 ${loading ? 'animate-pulse' : ''}`} />
          {loading ? 'Reading…' : report ? 'Re-read' : 'Run the report'}
        </Button>
        {report && (
          (report.count === 0 && (report.reviewer_violation_count || 0) === 0 && (report.team_warning_count || 0) === 0)
            ? <p className="text-sm text-emerald-700 dark:text-emerald-400" data-testid="signature-integrity-card-clean">✓ No signature problems found.</p>
            : <p className="text-sm text-amber-700 dark:text-amber-400" data-testid="signature-integrity-card-count">{report.count} employee-signature + {report.reviewer_violation_count || 0} reviewer-line + {report.team_warning_count || 0} team issue(s) — open the Evaluations page for the full list.</p>
        )}
      </CardContent>
    </Card>
  );
}


export default function SystemStatus() {
  const canView = useCap('admin', 'view');
  const canApplyMoney = useCap('admin', 'edit');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dry, setDry] = useState(null);
  const [dryRanAt, setDryRanAt] = useState(null);
  const [dryLoading, setDryLoading] = useState(false);
  const [moneyApply, setMoneyApply] = useState(null);
  const [moneyApplyLoading, setMoneyApplyLoading] = useState(false);

  const loadDryRun = useCallback(async () => {
    setDryLoading(true);
    try {
      const { data: res } = await systemStatusAPI.moneyDryRun();
      setDry(res);
      setDryRanAt(Date.now());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not run the dry run');
    } finally { setDryLoading(false); }
  }, []);

  const runMoneyApply = useCallback(async (dryRun) => {
    setMoneyApplyLoading(true);
    try {
      const { data: res } = await systemStatusAPI.moneyIntegerBackfill(dryRun);
      setMoneyApply(res);
      if (dryRun) {
        toast.success('Preview ready — nothing was written');
      } else {
        toast.success(`Added ${res.totals?.converted ?? 0} missing integer(s)`);
        loadDryRun();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Money backfill failed');
    } finally { setMoneyApplyLoading(false); }
  }, [loadDryRun]);

  // 'Reviewed, superseded' ruling on a superseded-W-4 / state-withholding money
  // disagreement. Reporting-only — the backend never touches a stored value and
  // rejects the ruling unless the record is actually superseded by a newer one.
  const [ruleTarget, setRuleTarget] = useState(null); // {collection, field, doc_id, employee}
  const [ruleNote, setRuleNote] = useState('');
  const [ruleSaving, setRuleSaving] = useState(false);

  const submitRuling = useCallback(async () => {
    if (!ruleTarget) return;
    setRuleSaving(true);
    try {
      await systemStatusAPI.moneyDisagreementRule({
        collection: ruleTarget.collection,
        doc_id: ruleTarget.doc_id,
        field: ruleTarget.field,
        note: ruleNote.trim(),
      });
      toast.success('Ruled "reviewed, superseded" — original values left unchanged');
      setRuleTarget(null);
      setRuleNote('');
      loadDryRun();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not record the ruling');
    } finally { setRuleSaving(false); }
  }, [ruleTarget, ruleNote, loadDryRun]);

  const [gaps, setGaps] = useState(null);
  const [gapsLoading, setGapsLoading] = useState(false);

  const loadGaps = useCallback(async () => {
    setGapsLoading(true);
    try {
      const { data: res } = await systemStatusAPI.moneyGaps();
      setGaps(res);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not list the unconverted records');
    } finally { setGapsLoading(false); }
  }, []);

  const [roleAccess, setRoleAccess] = useState(null);
  const [roleAccessLoading, setRoleAccessLoading] = useState(false);

  const loadRoleAccess = useCallback(async () => {
    setRoleAccessLoading(true);
    try {
      const { data: res } = await systemStatusAPI.sensitiveRoleAccess();
      setRoleAccess(res);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not read role access');
    } finally { setRoleAccessLoading(false); }
  }, []);

  const [rateVis, setRateVis] = useState(null);
  const [rateVisLoading, setRateVisLoading] = useState(false);

  const loadRateVis = useCallback(async () => {
    setRateVisLoading(true);
    try {
      const { data: res } = await systemStatusAPI.rateVisibility();
      setRateVis(res);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not read rate visibility');
    } finally { setRateVisLoading(false); }
  }, []);

  const [gridAccess, setGridAccess] = useState(null);
  const [gridAccessLoading, setGridAccessLoading] = useState(false);

  const loadGridAccess = useCallback(async () => {
    setGridAccessLoading(true);
    try {
      const { data: res } = await systemStatusAPI.schedulingGridAccess();
      setGridAccess(res);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not read who can open the grid');
    } finally { setGridAccessLoading(false); }
  }, []);


  const [malformed, setMalformed] = useState(null);
  const [malformedLoading, setMalformedLoading] = useState(false);

  const loadMalformed = useCallback(async () => {
    setMalformedLoading(true);
    try {
      const { data: res } = await systemStatusAPI.malformedDocuments();
      setMalformed(res);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not read the skipped documents');
    } finally { setMalformedLoading(false); }
  }, []);

  // v4.33.9: credential file-name backfill — dry run first, then apply.
  const [credDry, setCredDry] = useState(null);
  const [credResult, setCredResult] = useState(null);
  const [credLoading, setCredLoading] = useState(false);

  const runCredBackfill = useCallback(async (dry) => {
    setCredLoading(true);
    try {
      const { data: res } = await systemStatusAPI.credentialFileNamesBackfill(dry);
      if (dry) { setCredDry(res); setCredResult(null); }
      else { setCredResult(res); }
      toast.success(dry ? 'Dry run ready — nothing was written' : 'Credential file names applied');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not run the credential file-name backfill');
    } finally { setCredLoading(false); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await systemStatusAPI.get();
      setData(res);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not read system status');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (canView) load(); }, [canView, load]);

  if (!canView) {
    return (
      <div className="space-y-6" data-testid="system-status-page">
        <AccessState module="admin" action="view" items={[]} denied
          noAccessTitle="No access to System Status"
          noAccessDescription="System status is administration-only." />
      </div>
    );
  }

  const env = data?.environment;
  const isProd = env?.kind === 'production';
  const envUnknown = env?.status === 'unknown';
  const mig = data?.migrations;
  const cfg = data?.configuration;
  const aud = data?.audit_attribution;
  const money = data?.money_coverage;
  const txn = data?.transactions;
  const po = data?.po_numbering;
  const boot = data?.startup_tasks;
  const credTotal = credDry
    ? (credDry.updated?.eval_credentials || 0) + (credDry.updated?.licenses || 0) + (credDry.updated?.firearms_qualifications || 0)
    : 0;

  return (
    <div className="space-y-6" data-testid="system-status-page">
      {/* Environment banner — first thing on the page so the two can never be confused */}
      <div
        data-testid="environment-banner"
        className={`rounded-xl border-2 p-5 ${
          envUnknown ? 'border-slate-400 bg-slate-100 dark:bg-slate-900/50'
            : isProd ? 'border-red-500 bg-red-50 dark:bg-red-950/40'
              : 'border-amber-400 bg-amber-50 dark:bg-amber-950/30'}`}
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500">You are looking at</p>
            <p className={`text-4xl font-bold tracking-tight sm:text-5xl ${
              envUnknown ? 'text-slate-600' : isProd ? 'text-red-700 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'}`}
              data-testid="environment-label">
              {env?.label || 'Unknown'}
            </p>
            {env?.host && (
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400" data-testid="environment-host">
                {env.host} <span className="text-slate-400">· identified from {env.host_source}</span>
              </p>
            )}
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-widest text-slate-500">App version</p>
            <p className="text-3xl font-bold" data-testid="status-app-version">v{env?.version || '—'}</p>
            <p className="text-xs text-slate-500">
              released {env?.released || '—'} · build {env?.build || '—'}
            </p>
            <p className="text-xs text-slate-400">backend started {fmt(env?.build_at)}</p>
          </div>
        </div>
        {envUnknown && <div className="mt-4"><UnknownNote reason={env.reason} /></div>}
      </div>

      <FeatureStatusCard />
      <SsnKeyRotationCard />
      <ObjectStorageCard />
      <EmployeeNumberingCard />
      <SignatureIntegrityCard />


      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold"><Activity className="h-6 w-6" />System Status</h1>
          <p className="text-sm text-slate-500">
            Read only — nothing on this page changes anything. Generated {fmt(data?.generated_at)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="gap-1"><Eye className="h-3 w-3" />Read-only</Badge>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="refresh-status-btn">
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
        </div>
      </div>

      {loading && !data ? (
        <Card><CardContent className="p-8 text-center text-slate-400">Reading environment state...</CardContent></Card>
      ) : (
        <>
          {/* STARTUP TASKS (v4.33.9) — result of the last boot: index builds +
              migrations. Each step is isolated, so one failure never skips the
              others. Red if anything failed. */}
          <Card data-testid="startup-tasks-card">
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2"><Activity className="h-5 w-5" />Startup tasks</CardTitle>
                  <CardDescription>
                    Result of the last boot — index builds and migrations. Each step is isolated, so
                    one failure never skips the others.
                  </CardDescription>
                </div>
                {boot && boot.status !== 'unknown' && (
                  <StatusPill ok={boot.ok} okLabel="All OK" badLabel={`${boot.failed_count} failed`} />
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {!boot || boot.status === 'unknown' ? (
                <UnknownNote reason={boot?.reason || 'Startup task results are not available in this build.'} />
              ) : (
                <>
                  <div className="flex flex-wrap gap-2 text-sm">
                    <Badge variant="outline" data-testid="startup-last-boot">Last boot: {fmt(boot.last_boot_at)}</Badge>
                    <Badge variant="outline">{boot.step_count} recorded step(s)</Badge>
                    {!boot.complete && (
                      <Badge variant="outline" className="border-amber-400 text-amber-700">boot still running or interrupted</Badge>
                    )}
                  </div>
                  {boot.ok ? (
                    <p className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900 dark:bg-emerald-950/30" data-testid="startup-all-ok">
                      Every index and migration completed on the last boot.
                    </p>
                  ) : (
                    <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:bg-red-950/30" data-testid="startup-failures">
                      <p className="flex items-center gap-2 font-semibold"><XCircle className="h-4 w-4" />{boot.failed_count} step(s) failed on the last boot</p>
                      <ul className="ml-4 mt-1 list-disc space-y-1">
                        {boot.failed_steps.map((s, i) => (
                          <li key={i} data-testid={`startup-failed-${i}`}>
                            <span className="font-medium">{s.name}</span>{s.error ? ` — ${s.error}` : ''}
                            {typeof s.collisions === 'number' && s.collisions > 0 && (
                              <span className="ml-1 font-semibold">(collisions: {s.collisions})</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {(boot.steps || []).filter((s) => s.name === 'index:eval_credentials_state').map((s, i) => (
                    <p key={i} className="text-xs text-slate-500" data-testid="startup-cred-index">
                      Credential state index: {s.action || 'n/a'} · collisions {s.collisions ?? 0}
                    </p>
                  ))}
                </>
              )}
            </CardContent>
          </Card>

          {/* APPLY CREDENTIAL FILE NAMES (v4.33.9) — admin-only, metadata-only,
              idempotent, audited. Dry run first (counts), then apply. */}
          <Card data-testid="credential-backfill-card">
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2"><FlaskConical className="h-5 w-5" />Apply credential file names</CardTitle>
                  <CardDescription>
                    Retrofits data-driven display names onto already-uploaded credential files. Metadata
                    only — it never renames or rewrites a stored file. Idempotent and audited. Preview the
                    counts first, then apply.
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => runCredBackfill(true)} disabled={credLoading} data-testid="cred-backfill-dryrun-btn">
                    <FlaskConical className={`mr-2 h-4 w-4 ${credLoading ? 'animate-pulse' : ''}`} />
                    {credLoading ? 'Working…' : 'Dry run'}
                  </Button>
                  <Button onClick={() => runCredBackfill(false)} disabled={credLoading || !credDry} data-testid="cred-backfill-apply-btn">
                    Apply names
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {!credDry && !credResult ? (
                <p className="text-sm text-slate-500" data-testid="cred-backfill-idle">
                  Not run yet. Start with a dry run to see how many files would be named — nothing is written until you click “Apply names”.
                </p>
              ) : (
                <>
                  {credDry && (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="cred-dry-counts">
                      <div className="rounded-lg border p-3"><p className="text-xs text-slate-500">Eval credentials</p><p className="text-2xl font-bold" data-testid="cred-dry-evals">{credDry.updated?.eval_credentials ?? 0}</p></div>
                      <div className="rounded-lg border p-3"><p className="text-xs text-slate-500">License files</p><p className="text-2xl font-bold" data-testid="cred-dry-licenses">{credDry.updated?.licenses ?? 0}</p></div>
                      <div className="rounded-lg border p-3"><p className="text-xs text-slate-500">Firearms files</p><p className="text-2xl font-bold" data-testid="cred-dry-firearms">{credDry.updated?.firearms_qualifications ?? 0}</p></div>
                    </div>
                  )}
                  {credDry && !credResult && (
                    <p className="text-xs text-slate-500" data-testid="cred-dry-note">
                      Dry run — nothing was written. Click “Apply names” to write these {credTotal} display name(s).
                    </p>
                  )}
                  {credResult && (
                    <p className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900 dark:bg-emerald-950/30" data-testid="cred-apply-result">
                      Applied. Eval credentials: {credResult.updated?.eval_credentials ?? 0}, licenses: {credResult.updated?.licenses ?? 0}, firearms: {credResult.updated?.firearms_qualifications ?? 0}.
                    </p>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* MIGRATIONS */}
          {/* Configuration — which of the critical variables this deployment
              actually sets, and which are silently running on the preview .env
              baked into the image. NAMES AND SOURCES ONLY, never values. */}
          <Card data-testid="configuration-card">
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" />Configuration</CardTitle>
                  <CardDescription>
                    Where each security-critical value comes from in this environment. No value is read,
                    returned or displayed — only the name and its source.
                  </CardDescription>
                </div>
                {cfg?.status === 'ok' && (
                  <StatusPill
                    ok={cfg.all_critical_from_environment}
                    okLabel="All from the real environment"
                    badLabel={`${(cfg.from_dotenv_fallback?.length || 0) + (cfg.unset?.length || 0)} not set by this deployment`}
                  />
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {cfg?.status !== 'ok' ? (
                <UnknownNote reason={cfg?.reason || 'This backend did not report configuration sources.'} />
              ) : (
                <>
                  {cfg.unset?.length > 0 && (
                    <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950/30" data-testid="config-unset-warning">
                      <p className="flex items-center gap-2 font-semibold"><XCircle className="h-4 w-4" />Nothing sets {cfg.unset.length} critical variable(s)</p>
                      <p className="mt-1">{cfg.unset.join(', ')} — the code default applies. Read the row for what that means here.</p>
                    </div>
                  )}
                  {cfg.from_dotenv_fallback?.length > 0 && (
                    <div className="rounded-lg border border-amber-400 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/30" data-testid="config-fallback-warning">
                      <p className="flex items-center gap-2 font-semibold"><ShieldAlert className="h-4 w-4" />{cfg.from_dotenv_fallback.length} variable(s) come from the baked-in backend/.env</p>
                      <p className="mt-1">
                        {cfg.from_dotenv_fallback.join(', ')} — this deployment's own environment does not set them, so
                        they are running on the value built into the image from preview. Set them in this deployment's
                        secrets before backend/.env is removed from the build.
                      </p>
                    </div>
                  )}
                  <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Variable</TableHead>
                        <TableHead>Resolves</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>If nothing sets it</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(cfg.variables || []).map((v) => (
                        <TableRow key={v.name} data-testid={`config-row-${v.name}`}>
                          <TableCell className="font-mono text-xs">{v.name}</TableCell>
                          <TableCell>
                            {v.is_set
                              ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Yes</Badge>
                              : <Badge variant="destructive">No</Badge>}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={
                              v.source === 'environment' ? 'border-emerald-400 text-emerald-700'
                                : v.source === 'dotenv_file' ? 'border-amber-500 text-amber-700'
                                  : 'border-red-400 text-red-700'}
                              data-testid={`config-source-${v.name}`}>
                              {v.source_label}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-md text-xs text-slate-500">{v.effect_if_unset}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                  <p className="text-xs text-slate-500" data-testid="config-dotenv-presence">
                    backend/.env {cfg.dotenv_file_present ? 'IS present in this image' : 'is NOT present in this image'}.
                    {cfg.other_dotenv_fallback_names?.length > 0 && (
                      <> {cfg.other_dotenv_fallback_names.length} further variable(s) also resolve from it rather than
                      from this deployment's environment: {cfg.other_dotenv_fallback_names.join(', ')}.</>
                    )}
                  </p>
                  {cfg.startup_guard && (
                    <p className="text-xs text-slate-500" data-testid="config-startup-guard">
                      <strong>Startup guard:</strong>{' '}
                      {cfg.startup_guard.active ? 'ACTIVE' : 'not enforced'} — {cfg.startup_guard.note}
                    </p>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Audit attribution — is every new row readable back to a person? */}
          <Card data-testid="audit-attribution-card">
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2"><ScrollText className="h-5 w-5" />Audit attribution</CardTitle>
                  <CardDescription>
                    Whether each audit row can be read back to whoever caused it. v{aud?.cutover_version} is the
                    cutover: from that release the actor is written at the time of the action.
                  </CardDescription>
                </div>
                {aud?.status === 'ok' && (
                  <StatusPill ok={aud.unattributed_since_cutover === 0}
                    okLabel="Every new row attributed"
                    badLabel={`${aud.unattributed_since_cutover} unattributed since cutover`} />
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {aud?.status !== 'ok' ? (
                <UnknownNote reason={aud?.reason || 'This backend did not report audit attribution.'} />
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <div data-testid="audit-rows-total">
                      <p className="text-xs uppercase tracking-wide text-slate-500">audit rows</p>
                      <p className="text-lg font-semibold">{aud.rows_total?.toLocaleString()}</p>
                    </div>
                    <div data-testid="audit-rows-with-actor">
                      <p className="text-xs uppercase tracking-wide text-slate-500">with actor</p>
                      <p className="text-lg font-semibold">{aud.rows_with_actor?.toLocaleString()}</p>
                    </div>
                    <div data-testid="audit-rows-before-cutover">
                      <p className="text-xs uppercase tracking-wide text-slate-500">id only (pre-cutover)</p>
                      <p className="text-lg font-semibold">{aud.rows_before_cutover?.toLocaleString()}</p>
                    </div>
                    <div data-testid="audit-unattributed-since">
                      <p className="text-xs uppercase tracking-wide text-slate-500">unattributed since cutover</p>
                      <p className={`text-lg font-semibold ${aud.unattributed_since_cutover > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                        {aud.unattributed_since_cutover?.toLocaleString()}
                      </p>
                    </div>
                  </div>
                  {Object.keys(aud.actor_kinds || {}).length > 0 && (
                    <div className="flex flex-wrap gap-2" data-testid="audit-actor-kinds">
                      {Object.entries(aud.actor_kinds).map(([kind, count]) => (
                        <Badge key={kind} variant="outline"
                          className={kind === 'unattributed' ? 'border-red-400 text-red-700' : ''}>
                          {kind.replace('_', ' ')}: {count.toLocaleString()}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-slate-500" data-testid="audit-attribution-note">
                    {aud.note}
                    {aud.attribution_started_at && ` Attribution in this environment starts at ${new Date(aud.attribution_started_at).toLocaleString()}.`}
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          <Card data-testid="migrations-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><GitCommitHorizontal className="h-5 w-5" />Migrations</CardTitle>
              <CardDescription>One-shot data migrations recorded in this database.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {mig?.status === 'unknown' ? <UnknownNote reason={mig.reason} /> : (
                <>
                  {mig?.pinned && (
                    <div
                      data-testid="money-marker-row"
                      className={`rounded-lg border-2 p-4 ${mig.pinned.has_run
                        ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-950/30'
                        : 'border-amber-400 bg-amber-50 dark:bg-amber-950/30'}`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="font-mono text-sm font-semibold">{mig.pinned.name}</p>
                          <p className="text-xs text-slate-500">
                            {mig.pinned.has_run ? `Ran ${fmt(mig.pinned.ran_at)}` : 'No record of this migration in this database'}
                          </p>
                          {mig.pinned.note && <p className="mt-1 max-w-2xl text-xs text-slate-600 dark:text-slate-400">{mig.pinned.note}</p>}
                        </div>
                        <StatusPill ok={mig.pinned.has_run} okLabel="Ran" badLabel="Not Run" />
                      </div>
                      {Object.keys(mig.pinned.details || {}).length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {Object.entries(mig.pinned.details).map(([k, v]) => (
                            <Badge key={k} variant="outline" className="text-xs font-normal">{k}: {String(v)}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Migration</TableHead><TableHead>Status</TableHead><TableHead>When</TableHead><TableHead>Recorded detail</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {(mig?.entries || []).map(e => (
                        <TableRow key={e.name} data-testid={`migration-row-${e.name}`}>
                          <TableCell className="font-mono text-xs">{e.name}</TableCell>
                          <TableCell><StatusPill ok={e.has_run} okLabel="Ran" badLabel="Not Run" /></TableCell>
                          <TableCell className="text-sm">{e.ran_at_known ? fmt(e.ran_at) : <span className="text-slate-500">Unknown — no timestamp recorded</span>}</TableCell>
                          <TableCell className="text-xs text-slate-500">
                            {Object.entries(e.details || {}).slice(0, 4).map(([k, v]) => `${k}: ${v}`).join(' · ') || '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                      {(mig?.entries || []).length === 0 && (
                        <TableRow><TableCell colSpan={4} className="text-center text-sm text-slate-400">No other migrations recorded in this database</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </>
              )}
            </CardContent>
          </Card>

          {/* MONEY COVERAGE */}
          <Card data-testid="money-coverage-card">
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2"><Coins className="h-5 w-5" />Money conversion coverage</CardTitle>
                  <CardDescription>{money?.note}</CardDescription>
                </div>
                {money && (
                  <StatusPill
                    ok={money.complete}
                    okLabel="Complete in this environment"
                    badLabel={`${money.total_unconverted_documents} document(s) not converted`}
                    unknown={money.status === 'unknown'}
                  />
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {money?.status === 'unknown' ? <UnknownNote reason={money.reason} /> : (
                <>
                  {money?.unknown_fields?.length > 0 && (
                    <UnknownNote reason={`${money.unknown_fields.length} field(s) could not be counted: ${money.unknown_fields.join(', ')}. See the per-field rows below.`} />
                  )}
                  <p className="text-sm text-slate-500" data-testid="money-summary">
                    {money?.field_specs_checked} field specs checked ·{' '}
                    <strong>{money?.total_unconverted_documents}</strong> document(s) still hold a float with no integer
                  </p>
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Collection</TableHead><TableHead>Float field</TableHead><TableHead>Integer field</TableHead>
                      <TableHead className="text-right">Has float</TableHead><TableHead className="text-right">Missing integer</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {(money?.collections || []).map(c => (
                        <React.Fragment key={c.collection}>
                          <TableRow className="bg-slate-50 dark:bg-slate-900/40" data-testid={`money-collection-${c.collection}`}>
                            <TableCell colSpan={4} className="font-medium">{c.collection}</TableCell>
                            <TableCell className="text-right font-medium">
                              {c.unconverted_documents === 0
                                ? <span className="text-emerald-700">0</span>
                                : <span className="text-red-600">{c.unconverted_documents}</span>}
                            </TableCell>
                          </TableRow>
                          {c.fields.map(f => (
                            <TableRow key={`${c.collection}.${f.field}`}>
                              <TableCell />
                              <TableCell className="font-mono text-xs">{f.field}</TableCell>
                              <TableCell className="font-mono text-xs text-slate-500">{f.integer_field}</TableCell>
                              <TableCell className="text-right text-sm">{f.status === 'unknown' ? '—' : f.with_float}</TableCell>
                              <TableCell className="text-right text-sm">
                                {f.status === 'unknown'
                                  ? <span className="text-slate-500" title={f.reason}>Unknown</span>
                                  : f.missing_integer === 0
                                    ? <span className="text-emerald-700">0</span>
                                    : <span className="font-semibold text-red-600">{f.missing_integer}</span>}
                              </TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </CardContent>
          </Card>

          {/* TRANSACTIONS */}
          <Card data-testid="transactions-card">
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2"><Database className="h-5 w-5" />Database transactions</CardTitle>
                  <CardDescription>Multi-document transactions are required before any double-entry posting can run.</CardDescription>
                </div>
                {txn?.status === 'ok' && (
                  <StatusPill ok={txn.safe_for_finance} okLabel="Available" badLabel="Not available" />
                )}
              </div>
            </CardHeader>
            <CardContent>
              {txn?.status !== 'ok' ? <UnknownNote reason={txn?.reason || 'No probe result available.'} /> : (
                <>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-slate-500">Deployment</p>
                      <p className="text-lg font-semibold capitalize" data-testid="txn-deployment">{(txn.deployment || 'unknown').replace('_', ' ')}</p>
                    </div>
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-slate-500">Replica set name</p>
                      <p className="text-lg font-semibold" data-testid="txn-replset-name">{txn.replica_set_name || <span className="text-slate-500">Unknown</span>}</p>
                    </div>
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-slate-500">Member count</p>
                      <p className="text-lg font-semibold" data-testid="txn-member-count">{txn.member_count ?? <span className="text-slate-500">Unknown</span>}</p>
                    </div>
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-slate-500">Rollback verified</p>
                      <p className="text-lg font-semibold" data-testid="txn-rollback">{txn.rollback_verified ? 'Yes' : 'No'}</p>
                    </div>
                  </div>
                  {txn.error && (
                    <p className="mt-3 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950/30" data-testid="txn-error">{txn.error}</p>
                  )}
                  <p className="mt-3 text-xs text-slate-500">
                    {txn.probe_note} Source: {txn.source}, checked {fmt(txn.checked_at)}.
                  </p>
                </>
              )}
            </CardContent>
          </Card>
          {/* WHICH DOCUMENTS ARE NOT CONVERTED */}
          <Card data-testid="money-gaps-card">
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2"><ScrollText className="h-5 w-5" />Which records are not converted</CardTitle>
                  <CardDescription>
                    Names the documents behind the coverage count, with who last touched each one. Reads only — it writes nothing.
                  </CardDescription>
                </div>
                <Button variant="outline" onClick={loadGaps} disabled={gapsLoading} data-testid="load-money-gaps-btn">
                  <ScrollText className={`mr-2 h-4 w-4 ${gapsLoading ? 'animate-pulse' : ''}`} />
                  {gapsLoading ? 'Scanning...' : gaps ? 'Re-scan' : 'List the records'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!gaps ? (
                <p className="text-sm text-slate-500" data-testid="money-gaps-idle">
                  Not scanned yet. It walks every collection that holds money, so it is on demand rather than automatic.
                </p>
              ) : gaps.status === 'unknown' ? (
                <UnknownNote reason={gaps.reason} />
              ) : gaps.documents_total === 0 ? (
                <p className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800 dark:bg-emerald-950/30" data-testid="money-gaps-clean">
                  Nothing to report — every money value in this environment has its exact stored amount.
                </p>
              ) : (
                <>
                  <p className="text-sm text-slate-600 dark:text-slate-400" data-testid="money-gaps-summary">
                    <strong>{gaps.documents_total}</strong> record{gaps.documents_total === 1 ? '' : 's'} hold an amount that was saved without its exact value
                    {gaps.documents_total > gaps.documents_listed ? ` — showing the first ${gaps.documents_listed}` : ''}.
                  </p>
                  <div className="mt-3 space-y-3">
                    {gaps.documents.map((doc) => (
                      <div key={`${doc.collection}-${doc.id}`} className="rounded-lg border p-3" data-testid={`money-gap-${doc.id}`}>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold">{doc.label}</span>
                          <Badge variant="outline">{doc.collection}</Badge>
                          {doc.fields.map((f) => (
                            <Badge key={f.path} variant="outline" className="border-amber-500 text-amber-700">
                              {f.path} = {String(f.value)}
                            </Badge>
                          ))}
                        </div>
                        <p className="mt-1 text-xs text-slate-500">
                          {Object.entries(doc.timestamps).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${fmt(v)}`).join(' · ') || 'no timestamps on this record'}
                        </p>
                        {doc.audit_trail.length > 0 ? (
                          <ul className="mt-2 space-y-1 text-xs text-slate-600 dark:text-slate-400">
                            {doc.audit_trail.map((row, i) => (
                              <li key={i}>
                                <span className="font-medium">{row.action}</span> · {fmt(row.at)} · {row.who || 'unattributed'}
                                <span className="text-slate-400"> ({row.attribution})</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="mt-2 text-xs text-slate-400">No audit entries found for this record.</p>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 text-xs text-slate-500" data-testid="money-gaps-note">{gaps.note}</p>
                </>
              )}
            </CardContent>
          </Card>
          {/* WHO CAN READ PAY, BANKING AND TAX DATA */}
          <Card data-testid="sensitive-role-access-card">
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5" />Who can read pay, banking and tax data</CardTitle>
                  <CardDescription>
                    Every role that can reach a pay rate, a bank account or a tax election - and which layer granted it. HR, Finance, Admin and Auditor belong here. A custom role that only holds it because of a Permissions-screen override does not. Reads only.
                  </CardDescription>
                </div>
                <Button variant="outline" onClick={loadRoleAccess} disabled={roleAccessLoading} data-testid="load-sensitive-role-access-btn">
                  <ShieldAlert className={`mr-2 h-4 w-4 ${roleAccessLoading ? 'animate-pulse' : ''}`} />
                  {roleAccessLoading ? 'Reading...' : roleAccess ? 'Re-read' : 'List the roles'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!roleAccess ? (
                <p className="text-sm text-slate-500" data-testid="sensitive-role-access-idle">
                  Not read yet. It resolves every role through the same three permission layers the app enforces, so it is on demand.
                </p>
              ) : roleAccess.status === 'unknown' ? (
                <UnknownNote reason={roleAccess.reason} />
              ) : (
                <>
                  <p className="text-sm text-slate-600 dark:text-slate-400" data-testid="sensitive-role-access-summary">
                    <strong>{roleAccess.roles_with_sensitive_read}</strong> of {roleAccess.roles_total} roles can read this data
                    {' '}({roleAccess.custom_roles_with_sensitive_read} of {roleAccess.custom_roles_total} custom).
                    {roleAccess.disagreements > 0
                      ? <span className="font-semibold text-amber-700 dark:text-amber-500"> {roleAccess.disagreements} hold{roleAccess.disagreements === 1 ? 's' : ''} it only through an override.</span>
                      : <span className="text-emerald-700 dark:text-emerald-500"> None of them disagree with their own role definition.</span>}
                  </p>
                  <div className="mt-3 space-y-3">
                    {roleAccess.roles.map((r) => (
                      <div
                        key={r.role}
                        className={`rounded-lg border p-3 ${r.has_disagreement ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/20' : ''}`}
                        data-testid={`sensitive-role-${r.role}`}
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold">{r.label}</span>
                          <Badge variant="outline">{r.is_custom ? 'custom role' : 'built-in'}</Badge>
                          {r.has_disagreement && (
                            <Badge variant="destructive" data-testid={`sensitive-role-drift-${r.role}`}>role definition disagrees</Badge>
                          )}
                          <span className="text-xs text-slate-500">
                            {r.holders.primary.length} active account{r.holders.primary.length === 1 ? '' : 's'}
                            {r.holders.add_on.length > 0 ? ` + ${r.holders.add_on.length} as add-on` : ''}
                            {r.deactivated_holders?.total > 0 ? ` · ${r.deactivated_holders.total} deactivated` : ''}
                          </span>
                        </div>
                        <ul className="mt-2 space-y-1 text-xs text-slate-600 dark:text-slate-400">
                          {r.modules.map((m) => (
                            <li key={m.module}>
                              <span className="font-medium">{m.label}</span>
                              {' '}<span className="text-slate-400">({m.capabilities.join(', ')})</span>
                              {' - from '}{m.source}
                              {m.definition_disagrees && <strong className="text-amber-700 dark:text-amber-500"> - the role definition does not grant this</strong>}
                              <div className="text-slate-500">Shows: {m.exposes}</div>
                            </li>
                          ))}
                        </ul>
                        {r.override && (
                          <p className="mt-2 text-xs text-slate-500">
                            Override last saved {fmt(r.override.updated_at)} by {r.override.updated_by || 'unknown'}
                          </p>
                        )}
                        <p className="mt-1 text-xs text-slate-500">
                          {r.holders.primary.length > 0 ? `Held by: ${r.holders.primary.join(', ')}` : 'No active account currently holds this role'}
                          {r.holders.add_on.length > 0
                            ? ` · as an add-on role: ${r.holders.add_on.join(', ')}`
                            : ''}
                          {r.holders_who_can_decrypt.length > 0
                            ? ` · can also decrypt full values: ${r.holders_who_can_decrypt.join(', ')}`
                            : ' · nobody on it can decrypt the full values'}
                        </p>
                        {r.deactivated_holders?.total > 0 && (
                          <p className="mt-1 text-xs text-slate-400" data-testid={`sensitive-role-deactivated-${r.role}`}>
                            Deactivated, no access: {[...r.deactivated_holders.primary, ...r.deactivated_holders.add_on].join(', ')}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 text-xs text-slate-500" data-testid="sensitive-role-access-note">{roleAccess.note}</p>
                </>
              )}
            </CardContent>
          </Card>
          {/* WHO CAN SEE A PAY RATE, AND WHO CAN SEE A BILL RATE */}
          <Card data-testid="rate-visibility-card">
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5" />Who can see pay rates and bill rates</CardTitle>
                  <CardDescription>
                    Managers verify pay rates at schedule close, so pay rates are visible to them on a shift row. Bill rates are Finance, Payroll and Admin only - a holder sees them on a shift row too, and nobody else sees one anywhere. Either can be granted to one person without changing their role, and those grants are listed here by name. Reads only.
                  </CardDescription>
                </div>
                <Button variant="outline" onClick={loadRateVis} disabled={rateVisLoading} data-testid="load-rate-visibility-btn">
                  <ShieldAlert className={`mr-2 h-4 w-4 ${rateVisLoading ? 'animate-pulse' : ''}`} />
                  {rateVisLoading ? 'Reading...' : rateVis ? 'Re-read' : 'List the holders'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!rateVis ? (
                <p className="text-sm text-slate-500" data-testid="rate-visibility-idle">
                  Not read yet. It resolves every account through the access engine itself, so it is on demand.
                </p>
              ) : (
                <HoldersPanel data={rateVis} prefix="rate-visibility" />
              )}
            </CardContent>
          </Card>

          {/* WHO CAN OPEN THE WEEKLY COVERAGE GRID */}
          <Card data-testid="grid-access-card">
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2"><LayoutGrid className="h-5 w-5" />Who can open the weekly coverage grid</CardTitle>
                  <CardDescription>
                    The grid shipped switched off for everybody except Admin. Anyone without it opens Scheduling on the job and week screen, with no grid. Grant it to one person to pilot it on real jobs, or add it to a role from the Permissions screen. It is view-only - a holder still needs Scheduling: Edit to change a shift - and the server refuses the write as well as the screen hiding it. Reads only.
                  </CardDescription>
                </div>
                <Button variant="outline" onClick={loadGridAccess} disabled={gridAccessLoading} data-testid="load-grid-access-btn">
                  <LayoutGrid className={`mr-2 h-4 w-4 ${gridAccessLoading ? 'animate-pulse' : ''}`} />
                  {gridAccessLoading ? 'Reading...' : gridAccess ? 'Re-read' : 'List the holders'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!gridAccess ? (
                <p className="text-sm text-slate-500" data-testid="grid-access-idle">
                  Not read yet. It resolves every account through the access engine itself, so it is on demand.
                </p>
              ) : (
                <HoldersPanel data={gridAccess} prefix="grid-access" />
              )}
            </CardContent>
          </Card>

          {/* DOCUMENTS A LIST SCREEN HAD TO SKIP */}
          <Card data-testid="malformed-documents-card">
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5" />Records a list screen had to skip</CardTitle>
                  <CardDescription>
                    A document missing a field its screen requires is skipped so the rest of the list still loads, and listed here by collection and id. Nothing is repaired or deleted automatically - a missing value is a decision, not a default. A row disappears on the next read that finds its collection clean.
                  </CardDescription>
                </div>
                <Button variant="outline" onClick={loadMalformed} disabled={malformedLoading} data-testid="load-malformed-documents-btn">
                  <ShieldAlert className={`mr-2 h-4 w-4 ${malformedLoading ? 'animate-pulse' : ''}`} />
                  {malformedLoading ? 'Reading...' : malformed ? 'Re-read' : 'List them'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!malformed ? (
                <p className="text-sm text-slate-500" data-testid="malformed-documents-idle">
                  Nothing read yet.
                </p>
              ) : malformed.status === 'unknown' ? (
                <UnknownNote reason={malformed.reason} />
              ) : malformed.total === 0 ? (
                <p className="text-sm text-slate-600" data-testid="malformed-documents-none">
                  Nothing skipped. Every document a list screen read validated.
                </p>
              ) : (
                <>
                  <div className="space-y-3">
                    {malformed.collections.map((c) => (
                      <div key={c.collection} className="rounded-lg border p-3"
                        data-testid={`malformed-collection-${c.collection}`}>
                        <p className="text-sm font-medium">
                          {c.collection}
                          <span className="ml-2 text-xs text-slate-500">
                            {c.count} skipped
                          </span>
                        </p>
                        <ul className="mt-1.5 space-y-1">
                          {c.documents.map((d) => (
                            <li key={d.document_id} className="text-xs text-slate-600"
                              data-testid={`malformed-doc-${d.document_id}`}>
                              <span className="font-mono">{d.document_id}</span>
                              {' — missing '}
                              {(d.problems || []).map((p) => p.field).filter(Boolean).join(', ')}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 text-xs text-slate-500" data-testid="malformed-documents-note">{malformed.note}</p>
                </>
              )}
            </CardContent>
          </Card>
          {/* PO NUMBERING */}
          <Card data-testid="po-numbering-card">
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2"><Hash className="h-5 w-5" />PO numbering</CardTitle>
                  <CardDescription>
                    The counter that issues purchase order numbers, against the purchase orders already in this database.
                  </CardDescription>
                </div>
                {po?.status === 'ok' && (
                  <Badge
                    variant="outline"
                    className={
                      po.state === 'match' ? 'border-emerald-500 text-emerald-700'
                      : po.state === 'ahead' ? 'border-amber-500 text-amber-700'
                      : 'border-red-500 text-red-700'
                    }
                    data-testid="po-numbering-state">
                    {po.state === 'match' ? 'Match'
                      : po.state === 'ahead' ? 'Counter ahead (normal)'
                      : po.state === 'behind' ? 'Counter BEHIND the data'
                      : po.state === 'refused' ? 'Not seeded — malformed numbers'
                      : 'Not seeded'}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {po?.status !== 'ok' ? <UnknownNote reason={po?.reason || 'No PO numbering result available.'} /> : (
                <>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-slate-500">Counter value</p>
                      <p className="text-lg font-semibold" data-testid="po-counter-value">
                        {po.counter_exists ? po.counter_value : <span className="text-red-600">Not seeded</span>}
                      </p>
                    </div>
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-slate-500">Highest existing PO</p>
                      <p className="text-lg font-semibold" data-testid="po-highest-existing">
                        {po.highest_existing || <span className="text-slate-500">None</span>}
                      </p>
                    </div>
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-slate-500">Match</p>
                      <p className="text-lg font-semibold" data-testid="po-counter-match">{po.matches ? 'Yes' : 'No'}</p>
                    </div>
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-slate-500">Next number</p>
                      <p className="text-lg font-semibold" data-testid="po-counter-next">
                        {po.counter_next || <span className="text-slate-500">—</span>}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2 text-sm">
                    <Badge variant="outline" data-testid="po-numbers-matching">{po.numbers_matching_pattern} match {po.pattern}</Badge>
                    {po.numbers_blank > 0 && (
                      <Badge variant="outline" data-testid="po-numbers-blank">{po.numbers_blank} blank</Badge>
                    )}
                    <Badge
                      variant="outline"
                      className={po.numbers_malformed ? 'border-red-500 text-red-700' : ''}
                      data-testid="po-numbers-malformed">
                      {po.numbers_malformed} malformed
                    </Badge>
                    <Badge
                      variant="outline"
                      className={po.unique_index_present ? 'border-emerald-500 text-emerald-700' : 'border-red-500 text-red-700'}
                      data-testid="po-unique-index">
                      {po.unique_index_present ? 'Unique index on po_number' : 'NO unique index on po_number'}
                    </Badge>
                  </div>
                  {po.numbers_malformed > 0 && (
                    <p className="mt-3 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950/30" data-testid="po-malformed-warning">
                      {po.numbers_malformed} purchase order number(s) do not match {po.pattern} — for example {po.malformed_samples.join(', ')}.
                      The counter is deliberately NOT seeded from data it cannot parse, so creating a purchase order returns 503 until these are fixed.
                    </p>
                  )}
                  <p className="mt-3 text-xs text-slate-500" data-testid="po-numbering-note">{po.note}</p>
                </>
              )}
            </CardContent>
          </Card>
          {/* EMAIL MODE + WELCOME EMAILS */}
          {data?.welcome_email && (() => {
            const we = data.welcome_email;
            const env = we.email_environment || {};
            const level = env.warning_level;
            const banner = level === 'red'
              ? 'border-red-400 bg-red-50 text-red-800 dark:bg-red-950/30'
              : level === 'info'
              ? 'border-blue-300 bg-blue-50 text-blue-800 dark:bg-blue-950/30'
              : 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30';
            const toggleWelcome = async () => {
              try {
                const next = !we.welcome_emails_enabled;
                await systemStatusAPI.welcomeEmailsSet(next);
                toast.success(next ? 'Welcome emails turned ON' : 'Welcome emails turned OFF');
                load();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Could not change the setting');
              }
            };
            return (
              <Card data-testid="email-status-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><Mail className="h-5 w-5" />Email</CardTitle>
                  <CardDescription>How outbound email behaves in this environment, and the welcome-email switch.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Plain-words mode at the TOP of the email section */}
                  <div className={`rounded-lg border p-3 text-sm font-medium ${banner}`} data-testid="email-mode-banner">
                    {level === 'red' && <XCircle className="mr-1 inline h-4 w-4" />}
                    {env.headline}
                  </div>
                  {env.mode === 'preview' && Array.isArray(env.allowlist) && (
                    <p className="text-xs text-slate-500" data-testid="email-allowlist">
                      Allowlisted recipients: {env.allowlist.join(', ')}. Everyone else is logged and dropped, never sent.
                    </p>
                  )}
                  <div className="flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <p className="text-sm font-medium">Welcome emails</p>
                      <p className="text-xs text-slate-500" data-testid="welcome-emails-state">
                        {we.welcome_emails_enabled ? 'On' : 'Off'}
                        {we.backfill_complete === false && ' — backfill not complete, sending disabled'}
                        {env.mode === 'conflict' && ' — blocked by email-mode conflict'}
                      </p>
                    </div>
                    <Button
                      variant={we.welcome_emails_enabled ? 'outline' : 'default'}
                      onClick={toggleWelcome}
                      data-testid="toggle-welcome-emails-btn"
                    >
                      {we.welcome_emails_enabled ? 'Turn OFF' : 'Turn ON'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })()}
          {/* MONEY BACKFILL DRY RUN */}
          <Card data-testid="dry-run-card">
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2"><FlaskConical className="h-5 w-5" />Money backfill — dry run</CardTitle>
                  <CardDescription>
                    What the backfill WOULD do in this environment. Reads only — it writes nothing. Anything that writes appears in its own card, for approvers only.
                  </CardDescription>
                </div>
                <Button variant="outline" onClick={loadDryRun} disabled={dryLoading} data-testid="run-dry-run-btn">
                  <FlaskConical className={`mr-2 h-4 w-4 ${dryLoading ? 'animate-pulse' : ''}`} />
                  {dryLoading ? 'Scanning...' : dry ? 'Re-run' : 'Run dry run'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {!dry ? (
                <p className="text-sm text-slate-500">
                  Not run yet. It walks every document in every collection that holds money, so it is on demand rather than automatic.
                </p>
              ) : dry.status === 'unknown' ? <UnknownNote reason={dry.reason} /> : (
                <>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-slate-500">Would get a new integer</p>
                      <p className="text-2xl font-bold" data-testid="dry-would-convert">{dry.totals.would_convert}</p>
                    </div>
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-slate-500">Already match (no change)</p>
                      <p className="text-2xl font-bold text-emerald-700" data-testid="dry-already-matching">{dry.totals.already_matching}</p>
                    </div>
                    <div className={`rounded-lg border p-3 ${dry.totals.disagreeing ? 'border-red-400 bg-red-50 dark:bg-red-950/30' : ''}`}>
                      <p className="text-xs text-slate-500">Integer DISAGREES with float</p>
                      <p className={`text-2xl font-bold ${dry.totals.disagreeing ? 'text-red-600' : ''}`} data-testid="dry-disagreeing">{dry.totals.disagreeing}</p>
                      <p className="text-xs text-slate-400">Listed below, never overwritten</p>
                    </div>
                    <div className={`rounded-lg border p-3 ${dry.totals.precision_loss ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/30' : ''}`}>
                      <p className="text-xs text-slate-500">Would lose decimals</p>
                      <p className={`text-2xl font-bold ${dry.totals.precision_loss ? 'text-amber-700' : ''}`} data-testid="dry-precision-loss">{dry.totals.precision_loss}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 text-sm">
                    <Badge variant="outline">{dry.totals.field_specs} field specs</Badge>
                    <Badge variant="outline">{dry.totals.values_found} money values</Badge>
                    <Badge variant="outline">fully converted docs: {dry.totals.documents_fully_converted}</Badge>
                    <Badge variant="outline" className={dry.totals.documents_partially_converted ? 'border-amber-400 text-amber-700' : ''}>
                      partly converted docs: {dry.totals.documents_partially_converted}
                    </Badge>
                    <Badge variant="outline">unconverted docs: {dry.totals.documents_unconverted}</Badge>
                    {dry.totals.float_noise > 0 && (
                      <Badge variant="outline" className="border-slate-400 text-slate-600" data-testid="dry-noise-badge">
                        float noise tolerated: {dry.totals.float_noise}
                      </Badge>
                    )}
                    {dry.totals.reviewed_superseded > 0 && (
                      <Badge variant="outline" className="border-indigo-400 text-indigo-700" data-testid="dry-reviewed-superseded-badge">
                        reviewed, superseded: {dry.totals.reviewed_superseded}
                      </Badge>
                    )}
                    <Badge variant="outline">{dry.elapsed_ms} ms</Badge>
                  </div>

                  {dry.blockers?.length > 0 ? (
                    <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:bg-red-950/30" data-testid="dry-blockers">
                      <p className="font-semibold">Do not apply yet:</p>
                      <ul className="ml-4 list-disc">{dry.blockers.map((b, i) => <li key={i}>{b}</li>)}</ul>
                    </div>
                  ) : (
                    <p className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900 dark:bg-emerald-950/30" data-testid="dry-clean">
                      Nothing disagrees, nothing loses precision, everything converts cleanly. {dry.totals.would_convert === 0
                        ? 'And there is nothing left to convert in this environment.'
                        : `${dry.totals.would_convert} value(s) are waiting to be converted.`}
                    </p>
                  )}

                  {dry.totals.disagreeing > 0 && (
                    <div data-testid="dry-disagreement-list">
                      <h4 className="mb-2 font-medium text-red-700">Integer disagrees with the float — needs a ruling</h4>
                      <Table>
                        <TableHeader><TableRow>
                          <TableHead>Collection</TableHead><TableHead>Field</TableHead><TableHead>Document</TableHead>
                          <TableHead>Employee</TableHead>
                          <TableHead className="text-right">Float says</TableHead><TableHead className="text-right">Integer on file says</TableHead>
                          <TableHead className="text-right">Integer expected</TableHead>
                          {canApplyMoney && <TableHead className="text-right">Action</TableHead>}
                        </TableRow></TableHeader>
                        <TableBody>
                          {dry.collections.flatMap(c => c.fields.flatMap(f =>
                            f.disagreements.map(d => (
                              <TableRow key={`${c.collection}-${f.field}-${d.doc_id}`}>
                                <TableCell className="text-sm">{c.collection}</TableCell>
                                <TableCell className="font-mono text-xs">{f.field}</TableCell>
                                <TableCell className="font-mono text-xs">{d.doc_id}</TableCell>
                                <TableCell><EmployeeCell employee={d.employee} /></TableCell>
                                <TableCell className="text-right text-sm">{d.float_value}</TableCell>
                                <TableCell className="text-right text-sm font-semibold text-red-600">
                                  {d.integer_on_file}{d.on_file_renders_as != null && <span className="text-slate-500"> ({d.on_file_renders_as})</span>}
                                </TableCell>
                                <TableCell className="text-right text-sm">{d.integer_expected_from_float}</TableCell>
                                {canApplyMoney && (
                                  <TableCell className="text-right">
                                    {RULABLE_COLLECTIONS.has(c.collection) ? (
                                      <Button
                                        variant="outline" size="sm"
                                        data-testid={`rule-superseded-btn-${d.doc_id}`}
                                        onClick={() => { setRuleTarget({ collection: c.collection, field: f.field, doc_id: d.doc_id, employee: d.employee, float_value: d.float_value, integer_on_file: d.integer_on_file }); setRuleNote(''); }}
                                      >
                                        <Gavel className="mr-1 h-3 w-3" /> Rule superseded
                                      </Button>
                                    ) : <span className="text-xs text-slate-400">—</span>}
                                  </TableCell>
                                )}
                              </TableRow>
                            ))))}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                  {dry.reviewed_superseded?.count > 0 && (
                    <div data-testid="dry-reviewed-superseded-list">
                      <h4 className="mb-2 font-medium text-slate-700 dark:text-slate-300">
                        Reviewed, superseded — {dry.reviewed_superseded.count} ruled, excluded from the counts above
                      </h4>
                      <p className="mb-2 text-sm text-slate-600">{dry.reviewed_superseded.note}</p>
                      <div className="overflow-x-auto">
                      <Table>
                        <TableHeader><TableRow>
                          <TableHead>Collection</TableHead><TableHead>Field</TableHead><TableHead>Document</TableHead>
                          <TableHead>Employee</TableHead>
                          <TableHead className="text-right">Float</TableHead><TableHead className="text-right">Integer on file</TableHead>
                          <TableHead>Ruling</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                          {dry.reviewed_superseded.samples.map((r, i) => (
                            <TableRow key={`${r.collection}-${r.field}-${r.doc_id}-${i}`} data-testid={`reviewed-superseded-${r.doc_id}`}>
                              <TableCell className="text-sm">{r.collection}</TableCell>
                              <TableCell className="font-mono text-xs">{r.field}</TableCell>
                              <TableCell className="font-mono text-xs">{r.doc_id}</TableCell>
                              <TableCell><EmployeeCell employee={r.employee} /></TableCell>
                              <TableCell className="text-right text-sm">{r.float_value}</TableCell>
                              <TableCell className="text-right text-sm">{r.integer_on_file}</TableCell>
                              <TableCell className="text-xs text-slate-600">
                                <span className="italic">"{r.ruling?.note}"</span>
                                <span className="block text-slate-400">{r.ruling?.ruled_by_name} · {fmt(r.ruling?.ruled_at)}</span>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      </div>
                      {dry.reviewed_superseded.truncated && (
                        <p className="mt-1 text-xs text-slate-500">List capped at {dry.sample_cap}; the count is exact.</p>
                      )}
                    </div>
                  )}

                  {dry.totals.precision_loss > 0 && (
                    <div data-testid="dry-precision-list">
                      <h4 className="mb-2 font-medium text-amber-700">More decimals than the scale keeps — converting would round these</h4>
                      <Table>
                        <TableHeader><TableRow>
                          <TableHead>Collection</TableHead><TableHead>Field</TableHead><TableHead>Document</TableHead>
                          <TableHead className="text-right">Value</TableHead><TableHead className="text-right">Decimals</TableHead>
                          <TableHead className="text-right">Would become</TableHead><TableHead className="text-right">Would lose</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                          {dry.collections.flatMap(c => c.fields.flatMap(f =>
                            f.precision_samples.map(p => (
                              <TableRow key={`${c.collection}-${f.field}-${p.doc_id}`}>
                                <TableCell className="text-sm">{c.collection}</TableCell>
                                <TableCell className="font-mono text-xs">{f.field}</TableCell>
                                <TableCell className="font-mono text-xs">{p.doc_id}</TableCell>
                                <TableCell className="text-right text-sm">{p.value}</TableCell>
                                <TableCell className="text-right text-sm">{p.decimals_present} (keeps {p.decimals_kept})</TableCell>
                                <TableCell className="text-right text-sm">{p.round_trip}</TableCell>
                                <TableCell className="text-right text-sm font-semibold text-amber-700">{p.would_lose}</TableCell>
                              </TableRow>
                            ))))}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                  {dry.float_noise?.count > 0 && (
                    <div data-testid="dry-float-noise">
                      <h4 className="mb-2 font-medium text-slate-700 dark:text-slate-300">
                        Float noise, rounds cleanly — {dry.float_noise.count} value(s) tolerated
                      </h4>
                      <p className="mb-2 text-sm text-slate-600">{dry.float_noise.note}</p>
                      <div className="overflow-x-auto">
                      <Table>
                        <TableHeader><TableRow>
                          <TableHead>Collection</TableHead><TableHead>Field</TableHead><TableHead>Document</TableHead>
                          <TableHead className="text-right">Stored float</TableHead>
                          <TableHead className="text-right">Rounds to</TableHead>
                          <TableHead className="text-right">Lost</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                          {dry.float_noise.samples.map((n, i) => (
                            <TableRow key={`${n.collection}-${n.field}-${n.doc_id}-${i}`} data-testid={`float-noise-${n.doc_id}`}>
                              <TableCell className="text-sm">{n.collection}</TableCell>
                              <TableCell className="font-mono text-xs">{n.field}</TableCell>
                              <TableCell className="font-mono text-xs">{n.doc_id}</TableCell>
                              <TableCell className="text-right font-mono text-xs">{String(n.value)}</TableCell>
                              <TableCell className="text-right text-sm">{n.round_trip}</TableCell>
                              <TableCell className="text-right text-xs text-slate-500">{n.residual?.toExponential(1)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      </div>
                      {dry.float_noise.truncated && (
                        <p className="mt-1 text-xs text-slate-500">List capped at {dry.sample_cap}; the count is exact.</p>
                      )}
                    </div>
                  )}

                  {dry.po_consistency && (
                    <div data-testid="po-consistency">
                      <h4 className="mb-2 font-medium">Purchase orders still add up after conversion</h4>
                      {dry.po_consistency.status !== 'ok'
                        ? <UnknownNote reason={dry.po_consistency.reason} />
                        : dry.po_consistency.mismatch_count === 0
                          ? <p className="text-sm text-emerald-700" data-testid="po-consistency-clean">
                              Every line total sums to its subtotal, and subtotal plus tax equals the total
                              ({dry.po_consistency.purchase_orders_checked} checked
                              {dry.po_consistency.skipped_incomplete > 0 && `, ${dry.po_consistency.skipped_incomplete} skipped as incomplete`}).
                            </p>
                          : (
                            <>
                              <p className="mb-2 text-sm text-red-700">
                                <strong>{dry.po_consistency.mismatch_count}</strong> purchase order(s) do not add up. This blocks the apply.
                              </p>
                              <div className="overflow-x-auto">
                              <Table>
                                <TableHeader><TableRow>
                                  <TableHead>PO</TableHead><TableHead>Check</TableHead>
                                  <TableHead className="text-right">Computed</TableHead>
                                  <TableHead className="text-right">Stored</TableHead>
                                  <TableHead className="text-right">Delta</TableHead>
                                </TableRow></TableHeader>
                                <TableBody>
                                  {dry.po_consistency.mismatches.flatMap(m => m.problems.map((p, i) => (
                                    <TableRow key={`${m.po_id}-${i}`} data-testid={`po-consistency-${m.po_id}`}>
                                      <TableCell className="text-sm font-medium">{m.po_number}</TableCell>
                                      <TableCell className="font-mono text-xs">{p.check}</TableCell>
                                      <TableCell className="text-right text-sm">${(p.left_cents / 100).toFixed(2)}</TableCell>
                                      <TableCell className="text-right text-sm">${(p.right_cents / 100).toFixed(2)}</TableCell>
                                      <TableCell className="text-right text-sm font-semibold text-red-600">
                                        {p.delta_cents > 0 ? '+' : ''}{(p.delta_cents / 100).toFixed(2)}
                                      </TableCell>
                                    </TableRow>
                                  )))}
                                </TableBody>
                              </Table>
                              </div>
                            </>
                          )}
                    </div>
                  )}

                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Collection / field</TableHead>
                      <TableHead className="text-right">Values</TableHead>
                      <TableHead className="text-right">Would convert</TableHead>
                      <TableHead className="text-right">Already match</TableHead>
                      <TableHead className="text-right">Disagree</TableHead>
                      <TableHead className="text-right">Precision loss</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {dry.collections.filter(c => c.totals.values_found > 0 || c.status !== 'ok').map(c => (
                        <React.Fragment key={c.collection}>
                          <TableRow className="bg-slate-50 dark:bg-slate-900/40">
                            <TableCell className="font-medium">
                              {c.collection}
                              <span className="ml-2 text-xs font-normal text-slate-500">
                                {c.documents.scanned} scanned · {c.documents.fully_converted} full · {c.documents.partially_converted} partial · {c.documents.unconverted} none
                              </span>
                              {c.status !== 'ok' && <span className="ml-2 text-xs text-slate-600">Unknown — {c.reason}</span>}
                            </TableCell>
                            <TableCell className="text-right">{c.totals.values_found}</TableCell>
                            <TableCell className="text-right">{c.totals.would_convert}</TableCell>
                            <TableCell className="text-right text-emerald-700">{c.totals.already_matching}</TableCell>
                            <TableCell className="text-right">{c.totals.disagreeing || 0}</TableCell>
                            <TableCell className="text-right">{c.totals.precision_loss || 0}</TableCell>
                          </TableRow>
                          {c.fields.filter(f => f.values_found > 0).map(f => (
                            <TableRow key={`${c.collection}.${f.field}`}>
                              <TableCell className="pl-8 font-mono text-xs">{f.field} <span className="text-slate-400">→ {f.integer_field}</span></TableCell>
                              <TableCell className="text-right text-sm">{f.values_found}</TableCell>
                              <TableCell className="text-right text-sm">{f.would_convert}</TableCell>
                              <TableCell className="text-right text-sm text-emerald-700">{f.already_matching}</TableCell>
                              <TableCell className="text-right text-sm">{f.disagreeing ? <span className="font-semibold text-red-600">{f.disagreeing}</span> : 0}</TableCell>
                              <TableCell className="text-right text-sm">{f.precision_loss ? <span className="font-semibold text-amber-700">{f.precision_loss}</span> : 0}</TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                  {dry.po_tax_loss_suspects && (
                    <div data-testid="po-tax-loss">
                      <h4 className="mb-2 font-medium">
                        Purchase orders that may have already lost their sales tax
                      </h4>
                      {dry.po_tax_loss_suspects.status !== 'ok'
                        ? <UnknownNote reason={dry.po_tax_loss_suspects.reason} />
                        : dry.po_tax_loss_suspects.suspect_count === 0
                          ? <p className="text-sm text-emerald-700" data-testid="po-tax-loss-clean">
                              None. Every PO with tax has a total that includes it ({dry.po_tax_loss_suspects.purchase_orders_scanned} scanned).
                            </p>
                          : (
                            <>
                              <p className="mb-2 text-sm text-slate-600">
                                <strong>{dry.po_tax_loss_suspects.suspect_count}</strong> of {dry.po_tax_loss_suspects.purchase_orders_scanned} POs
                                record tax above zero but have a total equal to the subtotal. Listed only — nothing has been corrected.
                              </p>
                              <Table>
                                <TableHeader><TableRow>
                                  <TableHead>PO</TableHead><TableHead>Status</TableHead>
                                  <TableHead className="text-right">Subtotal</TableHead>
                                  <TableHead className="text-right">Tax recorded</TableHead>
                                  <TableHead className="text-right">Total stored</TableHead>
                                  <TableHead className="text-right">Total should be</TableHead>
                                </TableRow></TableHeader>
                                <TableBody>
                                  {dry.po_tax_loss_suspects.suspects.map(s => (
                                    <TableRow key={s.po_id} data-testid={`po-tax-loss-${s.po_id}`}>
                                      <TableCell className="text-sm font-medium">{s.po_number}</TableCell>
                                      <TableCell className="text-sm">{s.status}</TableCell>
                                      <TableCell className="text-right text-sm">${s.subtotal?.toLocaleString()}</TableCell>
                                      <TableCell className="text-right text-sm">${s.tax_amount?.toLocaleString()}</TableCell>
                                      <TableCell className="text-right text-sm font-semibold text-red-600">${s.total_amount?.toLocaleString()}</TableCell>
                                      <TableCell className="text-right text-sm">${s.expected_total_with_tax?.toLocaleString()}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </>
                          )}
                    </div>
                  )}

                  <p className="text-xs text-slate-500">
                    {dry.mode}. Lists are capped at {dry.sample_cap} rows each; the counts above are exact.
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          <Dialog open={!!ruleTarget} onOpenChange={(open) => { if (!open) { setRuleTarget(null); setRuleNote(''); } }}>
            <DialogContent data-testid="rule-superseded-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Gavel className="h-5 w-5" />Rule "reviewed, superseded"</DialogTitle>
                <DialogDescription>
                  This records that the disagreement is on a <strong>superseded historical record</strong> kept on
                  purpose — a newer active record is already in effect. It is <strong>reporting-only</strong>: the
                  original float and integer values are never changed, and the row is excluded from the
                  unresolved-disagreement and coverage counts. Rejected if the record is not actually superseded.
                </DialogDescription>
              </DialogHeader>
              {ruleTarget && (
                <div className="space-y-3 text-sm">
                  <div className="rounded-lg border bg-slate-50 p-3 dark:bg-slate-900/40">
                    <p><span className="text-slate-500">Collection:</span> <span className="font-mono text-xs">{ruleTarget.collection}</span></p>
                    <p><span className="text-slate-500">Field:</span> <span className="font-mono text-xs">{ruleTarget.field}</span></p>
                    <p><span className="text-slate-500">Document:</span> <span className="font-mono text-xs">{ruleTarget.doc_id}</span></p>
                    {ruleTarget.employee && <p><span className="text-slate-500">Employee:</span> {ruleTarget.employee.name} ({ruleTarget.employee.employee_number})</p>}
                    <p><span className="text-slate-500">Float:</span> {ruleTarget.float_value} · <span className="text-slate-500">Integer on file:</span> {ruleTarget.integer_on_file}</p>
                  </div>
                  <div>
                    <Label htmlFor="rule-note">Ruling note (required, at least 10 characters)</Label>
                    <Textarea
                      id="rule-note" data-testid="rule-superseded-note"
                      value={ruleNote} onChange={(e) => setRuleNote(e.target.value)}
                      placeholder="Why this superseded record is correct to keep as-is (e.g. replaced by the 2026 W-4 effective 2026-01-01)."
                      rows={3}
                    />
                    <p className="mt-1 text-xs text-slate-400">{ruleNote.trim().length}/10</p>
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => { setRuleTarget(null); setRuleNote(''); }} data-testid="rule-superseded-cancel">Cancel</Button>
                <Button
                  onClick={submitRuling}
                  disabled={ruleSaving || ruleNote.trim().length < 10}
                  data-testid="rule-superseded-submit"
                >
                  <Gavel className={`mr-2 h-4 w-4 ${ruleSaving ? 'animate-pulse' : ''}`} />
                  Record ruling
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {canApplyMoney && (
          <Card data-testid="money-apply-card">
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2"><Wrench className="h-5 w-5" />Money backfill — apply</CardTitle>
                  <CardDescription>
                    Adds the missing integer beside each money float. It only ever ADDS the integer — it never
                    changes a float, never overwrites an integer already on file, rounds half-up to the field's scale,
                    and is safe to run again (already-matching values are skipped). Anything that does not convert
                    exactly is left untouched and listed for a human ruling. Admin only.
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => runMoneyApply(true)} disabled={moneyApplyLoading} data-testid="money-apply-preview-btn">
                    <FlaskConical className={`mr-2 h-4 w-4 ${moneyApplyLoading ? 'animate-pulse' : ''}`} />
                    Preview counts
                  </Button>
                  <Button onClick={() => runMoneyApply(false)} disabled={moneyApplyLoading || !moneyApply} data-testid="money-apply-run-btn">
                    <Wrench className="mr-2 h-4 w-4" />
                    Apply — add missing integers
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {!moneyApply ? (
                <p className="text-sm text-slate-500" data-testid="money-apply-idle">
                  Not run yet. Start with “Preview counts” to see exactly how many integers would be added and which
                  values would be skipped — nothing is written until you click “Apply”.
                </p>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={moneyApply.dry_run ? 'outline' : 'default'} data-testid="money-apply-mode">{moneyApply.mode}</Badge>
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-slate-500">{moneyApply.dry_run ? 'Would add integer' : 'Integers added'}</p>
                      <p className="text-2xl font-bold" data-testid="money-apply-converted">{moneyApply.totals.converted}</p>
                    </div>
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-slate-500">Already match (skipped)</p>
                      <p className="text-2xl font-bold text-emerald-700" data-testid="money-apply-matching">{moneyApply.totals.already_matching}</p>
                    </div>
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-slate-500">Documents touched</p>
                      <p className="text-2xl font-bold" data-testid="money-apply-docs">{moneyApply.totals.docs_touched}</p>
                    </div>
                    <div className={`rounded-lg border p-3 ${moneyApply.did_not_convert_exactly.count ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/30' : ''}`}>
                      <p className="text-xs text-slate-500">Did NOT convert exactly</p>
                      <p className={`text-2xl font-bold ${moneyApply.did_not_convert_exactly.count ? 'text-amber-700' : ''}`} data-testid="money-apply-skipped">{moneyApply.did_not_convert_exactly.count}</p>
                      <p className="text-xs text-slate-400">left untouched</p>
                    </div>
                  </div>

                  {moneyApply.did_not_convert_exactly.count > 0 && (
                    <div data-testid="money-apply-skip-list">
                      <h4 className="mb-1 font-medium text-amber-700">Left for a human ruling</h4>
                      <p className="mb-2 text-xs text-slate-500">{moneyApply.did_not_convert_exactly.note}</p>
                      <div className="overflow-x-auto">
                      <Table>
                        <TableHeader><TableRow>
                          <TableHead>Collection</TableHead><TableHead>Field</TableHead><TableHead>Document</TableHead>
                          <TableHead>Employee</TableHead>
                          <TableHead>Reason</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                          {moneyApply.did_not_convert_exactly.values.map((v, i) => (
                            <TableRow key={`${v.collection}-${v.field}-${v.doc_id}-${i}`} data-testid={`money-apply-skip-${v.doc_id}-${i}`}>
                              <TableCell className="text-sm">{v.collection}</TableCell>
                              <TableCell className="font-mono text-xs">{v.field}</TableCell>
                              <TableCell className="font-mono text-xs">{v.doc_id}</TableCell>
                              <TableCell><EmployeeCell employee={v.employee} /></TableCell>
                              <TableCell className="text-xs text-slate-600">{v.reason}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      </div>
                      {moneyApply.did_not_convert_exactly.truncated && (
                        <p className="mt-1 text-xs text-slate-500">List capped at {moneyApply.sample_cap}; the count is exact.</p>
                      )}
                    </div>
                  )}

                  {moneyApply.unreadable_collections?.length > 0 && (
                    <p className="text-xs text-amber-700" data-testid="money-apply-unreadable">
                      Could not read: {moneyApply.unreadable_collections.join(', ')}.
                    </p>
                  )}

                  <p className="text-xs text-slate-500">
                    {moneyApply.dry_run
                      ? 'This was a preview — nothing was written. Click “Apply” to add the integers above.'
                      : 'Applied and recorded in the audit log. Run the dry run again to confirm the coverage is now clean.'}
                  </p>
                </>
              )}
            </CardContent>
          </Card>
          )}
        </>
      )}
    </div>
  );
}
